import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Divider from '@material-ui/core/Divider'

import { Error, LoadingButton, Subtitle } from '../../../../components'
import { subscriptionService } from '../../../../services'

import InterestConfirmedDialog from './InterestConfirmedDialog'
import ReturnTermDialog from './ReturnTermDialog'
import ReturnReceipt from './ReturnReceipt'

const useStyles = makeStyles(theme => ({
  buttons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'center',
    marginTop: '8px',
  },
  confirmedNote: {
    marginTop: '8px',
    textAlign: 'center',
    width: '100%',
  },
  divider: {
    margin: '24px 0 16px',
  },
  error: {
    marginTop: '8px',
  },
  returnButton: {
    backgroundColor: theme.palette.error?.main || '#8f2731',
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.error?.dark || '#6d1e26',
    },
  },
  subtitle: {
    fontWeight: 600,
    marginBottom: '0px',
  },
}))

const N3Allocation = ({
  classification,
  credentials,
  subscriptionUuid,
  onRefresh,
}) => {
  const classes = useStyles()

  const [confirmedInterestAt, setConfirmedInterestAt] = useState(null)
  const [confirmingInterest, setConfirmingInterest] = useState(false)
  const [interestDialogOpen, setInterestDialogOpen] = useState(false)

  const [termDialogOpen, setTermDialogOpen] = useState(false)
  const [returning, setReturning] = useState(false)
  const [receipt, setReceipt] = useState(null)
  const [downloadingReceipt, setDownloadingReceipt] = useState(false)

  const [error, setError] = useState(null)

  const {
    allocatedSchoolName,
    allocatedShiftName,
    firstOptionSchoolName,
    firstOptionShiftName,
    returnedToWaitingList,
    actionsAvailable,
  } = classification

  const interestConfirmedAt =
    confirmedInterestAt || classification.interestConfirmedAt

  const tryToConfirmInterest = async () => {
    setError(null)
    setConfirmingInterest(true)

    const { success, payload } = await subscriptionService.confirmInterest(
      credentials.cpf,
      credentials.cpfResponsible
    )

    setConfirmingInterest(false)

    if (success) {
      setConfirmedInterestAt(payload.interestConfirmedAt)
      setInterestDialogOpen(true)
    } else {
      setError(payload.error)
    }
  }

  const tryToReturnToWaitingList = async () => {
    setError(null)
    setReturning(true)

    const { success, payload } = await subscriptionService.returnToWaitingList(
      credentials.cpf,
      credentials.cpfResponsible
    )

    setReturning(false)
    setTermDialogOpen(false)

    if (success) {
      setReceipt(payload.receipt)

      if (onRefresh) onRefresh()
    } else {
      setError(payload.error)
    }
  }

  const tryToDownloadReceipt = async () => {
    setDownloadingReceipt(true)

    await subscriptionService.getReturnReceiptPdf(
      credentials.cpf,
      credentials.cpfResponsible,
      subscriptionUuid
    )

    setDownloadingReceipt(false)
  }

  if (receipt) {
    return (
      <>
        <Grid item xs={12}>
          <Divider className={classes.divider} />
        </Grid>

        <ReturnReceipt receipt={receipt} credentials={credentials} />
      </>
    )
  }

  return (
    <>
      <Grid item xs={12}>
        <Divider className={classes.divider} />
      </Grid>

      <Grid item xs={12}>
        <Subtitle className={classes.subtitle}>Importante</Subtitle>
      </Grid>

      {!returnedToWaitingList && (
        <Grid item xs={12}>
          <Typography variant="body1">
            Como as vagas nas escolas indicadas como preferência foram
            preenchidas, você foi contemplado(a) com uma vaga na escola{' '}
            <strong>{allocatedSchoolName}</strong>, no turno{' '}
            <strong>{allocatedShiftName}</strong>. A matrícula nessa unidade
            escolar é facultativa. Caso não tenha interesse nessa vaga, você
            poderá optar pelo retorno à lista de suplência da escola indicada
            na 1ª opção.
          </Typography>
        </Grid>
      )}

      {returnedToWaitingList && (
        <Grid item xs={12}>
          <Typography variant="subtitle2">
            O retorno à lista de suplência da escola de 1ª opção já foi
            solicitado para esta inscrição.
          </Typography>

          <div className={classes.buttons}>
            <LoadingButton
              disabled={downloadingReceipt}
              loading={downloadingReceipt}
              onClick={tryToDownloadReceipt}
            >
              Baixar comprovante de retorno em PDF
            </LoadingButton>
          </div>
        </Grid>
      )}

      {error && (
        <Grid item xs={12} className={classes.error}>
          <Error>{error}</Error>
        </Grid>
      )}

      {actionsAvailable && !returnedToWaitingList && (
        <Grid item xs={12}>
          <div className={classes.buttons}>
            <LoadingButton
              disabled={confirmingInterest || !!interestConfirmedAt}
              loading={confirmingInterest}
              onClick={tryToConfirmInterest}
            >
              Confirmo meu interesse nessa vaga
            </LoadingButton>

            <LoadingButton
              className={classes.returnButton}
              color="default"
              disabled={returning}
              loading={false}
              onClick={() => setTermDialogOpen(true)}
            >
              Retornar a lista de suplência da escola de 1ª opção
            </LoadingButton>
          </div>

          {interestConfirmedAt && (
            <Typography variant="body2" className={classes.confirmedNote}>
              Interesse na vaga confirmado com sucesso.
            </Typography>
          )}
        </Grid>
      )}

      <InterestConfirmedDialog
        open={interestDialogOpen}
        onClose={() => setInterestDialogOpen(false)}
      />

      <ReturnTermDialog
        open={termDialogOpen}
        loading={returning}
        schoolName={firstOptionSchoolName}
        shiftName={firstOptionShiftName}
        onCancel={() => setTermDialogOpen(false)}
        onConfirm={tryToReturnToWaitingList}
      />
    </>
  )
}

export default N3Allocation
